import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  dbLoadMessages, dbSendMessage, dbMarkMessagesRead,
  dbDeleteMessage, dbDeleteAllMessages, dbPurgeOldMessages,
  dbRecordBroadcastReceipt, dbLoadBroadcastReceipts,
} from "../db";
import { dbUpdateAmenityStatus } from "../db/messages";
import { playMessageChime } from "../utils/audioUtils";
import { hapticHeavy } from "../utils/haptics";
import { getSession, setSession } from "../utils/storageUtils";

export function useMessages({ session, myAirline, isUA, appDate, activeSCCO, partnerName }) {
  const [messages,        setMessages]        = useState([]);
  const [showMessages,    setShowMessages]    = useState(false);
  const [openThread,      setOpenThread]      = useState(null);
  const [urgentBanner,    setUrgentBanner]    = useState(null);
  const [seenUrgent,      setSeenUrgent]      = useState(() => {
    return getSession("sa_seen_urgent", []);
  });
  const [confirmDeleteAll,    setConfirmDeleteAll]    = useState(false);
  const [broadcastReceipts,   setBroadcastReceipts]   = useState([]);  // [{ message_id, airline, read_at }]

  const prevMessageIds      = useRef(new Set());
  const messagesInitialized = useRef(false); // true after first load, even if inbox was empty
  const hapticFiredFor      = useRef(new Set()); // ids we've already vibrated for; cleared on login

  const amenityStatusMap = useMemo(() => {
    const map = {};
    messages.forEach(m => {
      if (m.msg_type === "amenity_request" && m.connection_id) {
        if (!map[m.connection_id] || m.created_at > (map[m.connection_id].created_at || "")) {
          map[m.connection_id] = { status: m.amenity_status, types: m.amenity_types, msgId: m.id, created_at: m.created_at };
        }
      }
    });
    return map;
  }, [messages]);

  const unread = useMemo(
    () => messages.filter(m => {
      if (!m.read) {
        if (isUA) return m.to_airline === "UA";
        if (m.to_airline === "ALL") return !broadcastReceipts.some(r => r.message_id === m.id);
        return m.to_airline === myAirline;
      }
      return false;
    }).length,
    [messages, isUA, myAirline, broadcastReceipts]
  );

  const loadMessages = useCallback(() => {
    if (!session) return;
    dbLoadMessages().then(msgs => {
      setMessages(msgs);
      // Chime on new incoming messages
      const myAirlineId = isUA ? "UA" : myAirline;
      const newIncoming = msgs.filter(m => {
        const isIncoming = isUA
          ? m.to_airline === "UA"
          : (m.to_airline === myAirlineId || m.to_airline === "ALL");
        return isIncoming && !prevMessageIds.current.has(m.id);
      });
      if (messagesInitialized.current && newIncoming.length > 0) {
        const hasUrgent = newIncoming.some(m => m.urgent);
        playMessageChime(hasUrgent);
      }
      newIncoming.forEach(m => prevMessageIds.current.add(m.id));
      msgs.forEach(m => prevMessageIds.current.add(m.id));
      messagesInitialized.current = true;
      // Check for new urgent messages not yet seen
      const unseenUrgent = msgs.filter(m =>
        m.urgent && !m.read &&
        (isUA
          ? m.to_airline === "UA"
          : (m.to_airline === myAirline || m.to_airline === "ALL")
        ) &&
        !seenUrgent.includes(m.id)
      );
      if (unseenUrgent.length > 0) {
        const latest = unseenUrgent[unseenUrgent.length - 1];
        setUrgentBanner(latest);
        if (!hapticFiredFor.current.has(latest.id)) {
          hapticFiredFor.current.add(latest.id);
          hapticHeavy();
        }
      }
      // Load broadcast receipts for all users — partners see their own (RLS), UA sees all.
      // Running for both roles keeps UA's read-receipts panel current without closing/reopening.
      const broadcastIds = msgs.filter(m => m.to_airline === "ALL").map(m => m.id);
      if (broadcastIds.length) {
        dbLoadBroadcastReceipts(broadcastIds).then(rows => setBroadcastReceipts(rows));
      }
    });
  }, [session, myAirline, isUA, seenUrgent]);

  // Load messages on login and purge old ones
  useEffect(() => {
    if (session) { loadMessages(); dbPurgeOldMessages(); }
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally runs once on login; loadMessages is recreated on seenUrgent change but initial load should not re-fire

  // Poll for new messages every 15 seconds
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(loadMessages, 15000);
    return () => clearInterval(interval);
  }, [session, loadMessages]);


  // Listen for notification-click messages from the service worker
  // When user taps a push notification, SW posts NOTIFICATION_CLICK with the thread to open
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK" && event.data?.thread) {
        setOpenThread(event.data.thread);
        setShowMessages(true);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, []);

  const handleDismissUrgentBanner = useCallback(() => {
    if (!urgentBanner) return;
    const newSeen = [...seenUrgent, urgentBanner.id];
    setSeenUrgent(newSeen);
    setSession("sa_seen_urgent", newSeen);
    setUrgentBanner(null);
    dbMarkMessagesRead([urgentBanner.id]);
  }, [urgentBanner, seenUrgent]);

  const handleSendMessage = useCallback(async (msg) => {
    await dbSendMessage({
      ...msg,
      from_scco_name: isUA ? (activeSCCO ? activeSCCO.name : null) : (partnerName || null),
    });
    loadMessages();
  }, [isUA, activeSCCO, partnerName, loadMessages]);

  const handleBroadcast = useCallback(async (msg) => {
    await dbSendMessage({
      ...msg,
      to_airline: "ALL",
      urgent: true,
      from_scco_name: activeSCCO ? activeSCCO.name : null,
    });
    loadMessages();
  }, [activeSCCO, loadMessages]);

  const handleDeleteMessage = useCallback(async (id) => {
    await dbDeleteMessage(id);
    loadMessages();
  }, [loadMessages]);

  const handleDeleteAllMessages = useCallback(async () => {
    await dbDeleteAllMessages();
    loadMessages();
  }, [loadMessages]);

  const handleMarkRead = useCallback(async (ids) => {
    await dbMarkMessagesRead(ids);
    loadMessages();
  }, [loadMessages]);

  // Partner records that they've seen broadcast messages
  const handleRecordBroadcastReceipts = useCallback(async (messageIds) => {
    if (!myAirline || isUA || !messageIds?.length) return;
    // Optimistic update — clears the badge immediately, before the DB round-trip.
    // Must happen before the await so the badge clears even if the DB insert is slow or fails.
    setBroadcastReceipts(prev => {
      const existing = new Set(prev.map(r => r.message_id));
      const now = new Date().toISOString();
      const fresh = messageIds
        .filter(id => !existing.has(id))
        .map(id => ({ message_id: id, airline: myAirline, read_at: now }));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
    await Promise.all(messageIds.map(id => dbRecordBroadcastReceipt(id, myAirline))).catch(() => {});
  }, [myAirline, isUA]);

  const handleAmenityResponse = useCallback(async (msg, status, delayType, requestContactInfo = false, contactNames = [], denialReason = "") => {
    await dbUpdateAmenityStatus(msg.id, status, delayType);
    const label      = status === "approved" ? "approved" : "denied";
    const delayLabel = delayType === "controllable" ? "controllable delay" : "uncontrollable delay";
    const reasonSuffix = status === "denied" && denialReason ? ` — ${denialReason}` : "";
    await dbSendMessage({
      date: appDate,
      from_airline: "UA",
      to_airline: msg.from_airline,
      body: `Amenity request ${label} (${delayLabel})${reasonSuffix}.`,
      urgent: false,
      read: false,
      connection_id: msg.connection_id,
      from_scco_name: activeSCCO?.name || null,
    });
    if (requestContactInfo && status === "approved") {
      await dbSendMessage({
        date: appDate,
        from_airline: "UA",
        to_airline: msg.from_airline,
        body: contactNames.join("\n"),
        urgent: false,
        read: false,
        connection_id: msg.connection_id,
        from_scco_name: activeSCCO?.name || null,
        msg_type: "contact_info_request",
      });
    }
    loadMessages();
  }, [appDate, activeSCCO, loadMessages]);

  // UA loads receipts to see who has seen broadcasts
  const handleLoadBroadcastReceipts = useCallback(async (messageIds) => {
    if (!isUA || !messageIds?.length) return;
    const rows = await dbLoadBroadcastReceipts(messageIds);
    setBroadcastReceipts(rows);
  }, [isUA]);

  return {
    messages,
    showMessages, setShowMessages,
    openThread, setOpenThread,
    urgentBanner, setUrgentBanner,
    seenUrgent, setSeenUrgent,
    confirmDeleteAll, setConfirmDeleteAll,
    unread,
    loadMessages,
    handleDismissUrgentBanner,
    handleSendMessage,
    handleBroadcast,
    handleDeleteMessage,
    handleDeleteAllMessages,
    handleMarkRead,
    broadcastReceipts,
    handleRecordBroadcastReceipts,
    handleLoadBroadcastReceipts,
    amenityStatusMap,
    handleAmenityResponse,
  };
}
